import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Modal from 'react-modal';
import { galleriesApi } from '../../../api/adminApi';
import { galleryId, galleryImageUrls, galleryImageCount, parseGalleryImageEntries } from './galleryHelpers';
import './GalleriesList.css';

const DEFAULT_LIMIT = 100;

function GalleriesList() {
  const [galleries, setGalleries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(DEFAULT_LIMIT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formModalOpen, setFormModalOpen] = useState(false);
  /** null = add gallery; set = edit that row */
  const [editingGallery, setEditingGallery] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [formFiles, setFormFiles] = useState([]);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteAllBusy, setDeleteAllBusy] = useState(false);

  const loadGalleries = (pageNum = page) => {
    setLoading(true);
    setError('');
    galleriesApi
      .list(pageNum, limit)
      .then((res) => {
        setGalleries(res.list);
        setTotal(res.total);
        setPage(res.page);
      })
      .catch((err) => setError(err.userMessage || 'Failed to load galleries'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadGalleries(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddModal = () => {
    setEditingGallery(null);
    setFormTitle('');
    setFormFiles([]);
    setFormError('');
    setFormModalOpen(true);
  };

  const openEditModal = (g) => {
    const gid = galleryId(g);
    if (!gid) return;
    setEditingGallery(g);
    setFormTitle(g.title ?? '');
    setFormFiles([]);
    setFormError('');
    setFormModalOpen(true);
    galleriesApi
      .getOne(gid)
      .then((full) => {
        if (!full) return;
        setEditingGallery((prev) => (galleryId(prev) === gid ? full : prev));
      })
      .catch(() => {});
  };

  const closeFormModal = () => {
    setFormModalOpen(false);
    setEditingGallery(null);
  };

  const handleFilesChange = (e) => {
    const list = e.target.files ? Array.from(e.target.files) : [];
    setFormFiles(list);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    if (!formTitle.trim()) {
      setFormError('Title is required.');
      return;
    }

    if (editingGallery) {
      const gid = galleryId(editingGallery);
      if (!gid) return;
      const trimmed = formTitle.trim();
      const hasFiles = formFiles.length > 0;
      const titleChanged = trimmed !== (editingGallery.title ?? '').trim();
      if (!titleChanged && !hasFiles) {
        setFormError('Change the title or choose images to append.');
        return;
      }
      const payload = {};
      if (titleChanged) payload.title = trimmed;
      if (hasFiles) payload.images = formFiles;
      setFormSaving(true);
      galleriesApi
        .update(gid, payload)
        .then(() => {
          closeFormModal();
          loadGalleries(page);
        })
        .catch((err) => setFormError(err.userMessage || 'Something went wrong.'))
        .finally(() => setFormSaving(false));
      return;
    }

    setFormSaving(true);
    galleriesApi
      .create({ title: formTitle.trim(), images: formFiles })
      .then(() => {
        closeFormModal();
        loadGalleries(page);
      })
      .catch((err) => setFormError(err.userMessage || 'Something went wrong.'))
      .finally(() => setFormSaving(false));
  };

  const handleDeleteClick = (g) => setDeleteTarget(g);
  const handleDeleteCancel = () => setDeleteTarget(null);
  const handleDeleteConfirm = () => {
    const id = galleryId(deleteTarget);
    if (!id) return;
    setDeleting(true);
    galleriesApi
      .delete(id)
      .then(() => {
        setDeleteTarget(null);
        loadGalleries(page);
      })
      .catch((err) => setError(err.userMessage || 'Failed to delete'))
      .finally(() => setDeleting(false));
  };

  const openDeleteAll = () => {
    setDeleteAllOpen(true);
  };

  const closeDeleteAll = () => {
    if (deleteAllBusy) return;
    setDeleteAllOpen(false);
  };

  const handleDeleteAllConfirm = () => {
    setDeleteAllBusy(true);
    setError('');
    galleriesApi
      .deleteAll()
      .then(() => {
        closeDeleteAll();
        loadGalleries(1);
      })
      .catch((err) => {
        setError(err.userMessage || 'Failed to delete all galleries');
        closeDeleteAll();
      })
      .finally(() => setDeleteAllBusy(false));
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const isEditing = editingGallery != null;
  const editingImageEntries = isEditing && editingGallery ? parseGalleryImageEntries(editingGallery) : [];

  return (
    <div className="admin-galleries">
      <div className="admin-galleries-header">
        <h2>Galleries</h2>
        <div className="admin-galleries-header-actions">
          <button
            type="button"
            className="admin-galleries-delete-all"
            onClick={openDeleteAll}
            disabled={loading || galleries.length === 0}
          >
            Delete all galleries
          </button>
          <button type="button" className="admin-galleries-add" onClick={openAddModal}>
            Add gallery
          </button>
        </div>
      </div>

      {error && (
        <div className="admin-galleries-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="admin-galleries-loading">Loading galleries…</div>
      ) : (
        <div className="admin-galleries-table-wrap">
          <table className="admin-galleries-table">
            <thead>
              <tr>
                <th>Preview</th>
                <th>Title</th>
                <th>Images</th>
                <th>Added</th>
                <th className="admin-galleries-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {galleries.length === 0 ? (
                <tr>
                  <td colSpan={5} className="admin-galleries-empty">
                    No galleries yet.{' '}
                    <button type="button" className="admin-galleries-empty-btn" onClick={openAddModal}>
                      Add one
                    </button>
                    .
                  </td>
                </tr>
              ) : (
                galleries.map((g) => {
                  const gid = galleryId(g);
                  const urls = galleryImageUrls(g);
                  const thumb = urls[0];
                  const count = galleryImageCount(g);
                  return (
                    <tr key={gid || g.title}>
                      <td>
                        {thumb ? (
                          <img src={thumb} alt="" className="admin-galleries-thumb" />
                        ) : (
                          <div className="admin-galleries-thumb-placeholder" aria-hidden>
                            —
                          </div>
                        )}
                      </td>
                      <td className="admin-galleries-title-cell">{g.title || '—'}</td>
                      <td>{count}</td>
                      <td className="admin-galleries-date-cell">
                        {g.created_at
                          ? new Date(g.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : '—'}
                      </td>
                      <td className="admin-galleries-actions-col">
                        {gid ? (
                          <Link to={`/admin/galleries/${gid}`} className="admin-galleries-btn admin-galleries-btn-view">
                            View
                          </Link>
                        ) : null}
                        <button
                          type="button"
                          className="admin-galleries-btn admin-galleries-btn-edit-gallery"
                          onClick={() => openEditModal(g)}
                          disabled={!gid}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="admin-galleries-btn admin-galleries-btn-delete"
                          onClick={() => handleDeleteClick(g)}
                          disabled={!gid}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > 0 && totalPages > 1 && (
        <div className="admin-galleries-pagination">
          <button
            type="button"
            className="admin-galleries-page-btn"
            onClick={() => loadGalleries(page - 1)}
            disabled={page <= 1}
          >
            Previous
          </button>
          <span className="admin-galleries-page-info">
            Page {page} of {totalPages} ({total} total)
          </span>
          <button
            type="button"
            className="admin-galleries-page-btn"
            onClick={() => loadGalleries(page + 1)}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      )}

      <Modal
        isOpen={formModalOpen}
        onRequestClose={() => {
          if (!formSaving) closeFormModal();
        }}
        contentLabel={isEditing ? 'Edit gallery' : 'Add gallery'}
        className="admin-galleries-modal-content"
        overlayClassName="admin-galleries-modal-overlay"
      >
        <div className="admin-galleries-modal-inner">
          <h3>{isEditing ? 'Edit gallery' : 'Add gallery'}</h3>
          {isEditing && (
            <p className="admin-galleries-modal-lead">
              Update the title and/or append images. To remove photos, open <strong>View</strong>.
            </p>
          )}
          <form onSubmit={handleFormSubmit}>
            {formError && (
              <div className="admin-galleries-form-error" role="alert">
                {formError}
              </div>
            )}

            {isEditing && editingImageEntries.length > 0 && (
              <div className="admin-galleries-edit-current">
                <p className="admin-galleries-edit-current-label">
                  Current photos ({editingImageEntries.length})
                </p>
                <ul className="admin-galleries-edit-thumb-grid" aria-label="Current gallery photos">
                  {editingImageEntries.map((img) => (
                    <li key={img.key}>
                      <img src={img.url} alt="" className="admin-galleries-edit-thumb" />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="admin-galleries-form-field">
              <label htmlFor="form-gallery-title">Title *</label>
              <input
                id="form-gallery-title"
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="e.g. Spring showcase"
                disabled={formSaving}
              />
            </div>

            <div className="admin-galleries-form-field">
              <label htmlFor="form-gallery-images">{isEditing ? 'Append images' : 'Images'}</label>
              <input
                id="form-gallery-images"
                type="file"
                accept="image/*"
                multiple
                onChange={handleFilesChange}
                disabled={formSaving}
              />
              {formFiles.length > 0 && (
                <p className="admin-galleries-files-hint">
                  {formFiles.length} file(s) selected{isEditing ? ' (uploaded on save)' : ''}
                </p>
              )}
            </div>

            <div className="admin-galleries-form-actions">
              <button
                type="button"
                className="admin-galleries-form-cancel"
                onClick={closeFormModal}
                disabled={formSaving}
              >
                Cancel
              </button>
              <button type="submit" className="admin-galleries-form-submit" disabled={formSaving}>
                {formSaving
                  ? isEditing
                    ? 'Saving…'
                    : 'Creating…'
                  : isEditing
                    ? 'Save changes'
                    : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {deleteTarget && (
        <div className="admin-galleries-delete-modal" role="dialog" aria-modal="true" aria-labelledby="delete-g-title">
          <div className="admin-galleries-delete-backdrop" onClick={handleDeleteCancel} aria-hidden="true" />
          <div className="admin-galleries-delete-box">
            <h3 id="delete-g-title">Delete gallery?</h3>
            <p>
              This soft-deletes &ldquo;{deleteTarget.title || 'this gallery'}&rdquo; and removes its images from
              storage. This cannot be undone from the admin UI.
            </p>
            <div className="admin-galleries-delete-actions">
              <button
                type="button"
                className="admin-galleries-btn admin-galleries-btn-cancel"
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-galleries-btn admin-galleries-btn-delete"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteAllOpen && (
        <div className="admin-galleries-delete-modal" role="dialog" aria-modal="true" aria-labelledby="delete-all-title">
          <div className="admin-galleries-delete-backdrop" onClick={closeDeleteAll} aria-hidden="true" />
          <div className="admin-galleries-delete-box">
            <h3 id="delete-all-title">Delete all galleries?</h3>
            <p>
              Every gallery will be soft-deleted and all image files will be removed from storage. This cannot be undone
              from the admin UI.
            </p>
            <div className="admin-galleries-delete-actions">
              <button
                type="button"
                className="admin-galleries-btn admin-galleries-btn-cancel"
                onClick={closeDeleteAll}
                disabled={deleteAllBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-galleries-btn admin-galleries-btn-delete"
                onClick={handleDeleteAllConfirm}
                disabled={deleteAllBusy}
              >
                {deleteAllBusy ? 'Deleting…' : 'Delete all'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GalleriesList;
